// [PURPOSE]: Pure, system-agnostic message builders for party-panel mechanical
//   interactions (loot item/currency claim, DM-status claim/release, DM-shared
//   shop visit). Each returns a { message, type, details } tuple ready to pass
//   to logActivity(message, type, details) (Rule 13).
// [WHY]: A33 — PartyPanel emitted ZERO logActivity (grep-verified 2026-06-01);
//   party-specific mechanical actions were invisible in the activity log while
//   the "normal" panels logged broadly (124 src sites). Extracted to a util so
//   production (App.jsx party handlers) and the test import the SAME builders
//   (Rule 22 — no inline replica that could pass while production drifts).
// [INTEGRATION]: the `type` values (items / currency / system) already map
//   through src/utils/activityLogFilters.js ACTIVITY_TYPE_TO_FILTER, so no new
//   filter category is needed. Naming is system-agnostic (no D&D-specific verbs
//   beyond "Dungeon Master", which is itself a label) so the endgame
//   multi-system engine can reuse the party-event taxonomy.

// Short currency labels; unknown keys pass through verbatim.
const CURRENCY_LABELS = Object.freeze({ pp: 'pp', gp: 'gp', ep: 'ep', sp: 'sp', cp: 'cp' });

/**
 * Message for a single loot item claimed from party-shared loot.
 * @param {string} itemName
 * @param {number} [quantity=1]
 * @returns {{ message: string, type: 'items', details: object }}
 */
export function lootItemClaimedMessage(itemName, quantity = 1) {
  const safeName = (typeof itemName === 'string' && itemName.trim()) ? itemName.trim() : 'item';
  const qty = Number.isFinite(quantity) ? quantity : 1;
  const qtyLabel = qty > 1 ? ` ×${qty}` : '';
  return {
    message: `Claimed party loot: ${safeName}${qtyLabel}`,
    type: 'items',
    details: { itemName: safeName, quantity: qty, source: 'party-loot' },
  };
}

/**
 * Message for currency claimed from party-shared loot, honoring an optional
 * even split across the party (shareCount > 1 → per-member floor share).
 * @param {Record<string, number>} currencyMap  e.g. { gp: 100, sp: 5 }
 * @param {number} [shareCount=1]
 * @returns {{ message: string, type: 'currency', details: object }}
 */
export function lootCurrencyClaimedMessage(currencyMap, shareCount = 1) {
  const entries = (currencyMap && typeof currencyMap === 'object')
    ? Object.entries(currencyMap).filter(([, a]) => typeof a === 'number' && a !== 0)
    : [];
  const divisor = (Number.isFinite(shareCount) && shareCount > 1) ? shareCount : 1;
  const share = {};
  const parts = entries.map(([t, a]) => {
    const amount = Math.floor(a / divisor);
    share[t] = amount;
    return `${amount} ${CURRENCY_LABELS[t] || t}`;
  });
  const splitLabel = divisor > 1 ? ` (split ${divisor} ways)` : '';
  return {
    message: parts.length
      ? `Claimed party loot: ${parts.join(', ')}${splitLabel}`
      : 'Claimed party loot currency',
    type: 'currency',
    details: { currency: share, shareCount: divisor, source: 'party-loot' },
  };
}

/**
 * Message for claiming or releasing the Dungeon Master role.
 * @param {boolean} claimed  true = claimed, false = released
 * @returns {{ message: string, type: 'system', details: object }}
 */
export function dmStatusMessage(claimed) {
  return {
    message: claimed ? 'Claimed Dungeon Master role' : 'Released Dungeon Master role',
    type: 'system',
    details: { dm: !!claimed, source: 'party' },
  };
}

/**
 * Message for opening / visiting a DM-shared shop pushed to the party.
 * @param {string} shopName
 * @returns {{ message: string, type: 'system', details: object }}
 */
export function pushedShopVisitedMessage(shopName) {
  const safe = (typeof shopName === 'string' && shopName.trim()) ? shopName.trim() : 'shop';
  return {
    message: `Opened DM-shared shop: ${safe}`,
    type: 'system',
    details: { shopName: safe, source: 'party' },
  };
}
